import {
  Alert,
  Anchor,
  Box,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Text,
  ThemeIcon,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { closeAllModals, openConfirmModal } from '@mantine/modals';
import {
  IconArchive,
  IconArrowsLeftRight,
  IconBabyCarriage,
  IconBan,
  IconBolt,
  IconBookmark,
  IconBrush,
  IconCircleMinus,
  IconClock,
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconSpeakerphone,
  IconExclamationMark,
  IconFlag,
  IconLock,
  IconLockOff,
  IconPlus,
  IconRadar2,
  IconRecycle,
  IconReload,
  IconRepeat,
  IconRosetteDiscountCheck,
  IconShieldCheck,
  IconTagOff,
  IconTrash,
} from '@tabler/icons-react';
import { truncate } from 'lodash-es';
import type { InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { getEdgeUrl } from '~/client-utils/cf-images-utils';
import { notifyIfPublishHeld } from '~/utils/model-publish-toast';
import { OwnerRatingControls } from '~/components/RatingReview/OwnerRatingControls';
import { RenderAdUnitOutstream } from '~/components/Ads/AdUnitOutstream';
import { AlertWithIcon } from '~/components/AlertWithIcon/AlertWithIcon';
import { NotFound } from '~/components/AppLayout/NotFound';
import { AssociatedModels } from '~/components/AssociatedModels/AssociatedModels';
import {
  InteractiveTipBuzzButton,
  useBuzzTippingStore,
} from '~/components/Buzz/InteractiveTipBuzzButton';
import { ButtonTooltip } from '~/components/CivitaiWrapped/ButtonTooltip';
import { Collection } from '~/components/Collection/Collection';
import { openAddToCollectionModal } from '~/components/Dialog/triggers/add-to-collection';
import { openAddToHubModal } from '~/components/Dialog/triggers/add-to-hub';
import { openBlockModelTagsModal } from '~/components/Dialog/triggers/block-model-tags';
import { openReportModal } from '~/components/Dialog/triggers/report';
import { RequestLockReviewMenuItem } from '~/components/Model/LockReview/RequestLockReview';
import { openUnpublishModal } from '~/components/Dialog/triggers/unpublish';
import { HelpButton } from '~/components/HelpButton/HelpButton';
import dynamic from 'next/dynamic';
import { dialogStore } from '~/components/Dialog/dialogStore';
import { ModelPromotionModal } from '~/components/Promotion/ModelPromotionModal';

const MigrateModelToCollection = dynamic(
  () => import('~/components/Model/Actions/MigrateModelToCollection'),
  { ssr: false }
);
const MergeVersions = dynamic(() => import('~/components/Model/Actions/MergeVersions'), {
  ssr: false,
});
const TransferModelOwnership = dynamic(
  () => import('~/components/Model/Actions/TransferModelOwnership'),
  { ssr: false }
);
import { HideModelButton } from '~/components/HideModelButton/HideModelButton';
import { BlockUserButton } from '~/components/HideUserButton/BlockUserButton';
import { HideUserButton } from '~/components/HideUserButton/HideUserButton';
import { StatHoverCard } from '~/components/Stats/StatHoverCard';
import { useQueryImages } from '~/components/Image/image.utils';
import { InfoPopover } from '~/components/InfoPopover/InfoPopover';
// import { ImageFiltersDropdown } from '~/components/Image/Infinite/ImageFiltersDropdown';
import { LoginRedirect } from '~/components/LoginRedirect/LoginRedirect';
import { AddToCollectionMenuItem } from '~/components/MenuItems/AddToCollectionMenuItem';
import { AddToHubMenuItem } from '~/components/MenuItems/AddToHubMenuItem';
import { ToggleSearchableMenuItem } from '~/components/MenuItems/ToggleSearchableMenuItem';
import { Gated } from '~/components/Gated/Gated';
import { buildBreadcrumbSchema } from '~/components/Meta/site-schema';
import { modelOgEndpoint } from '~/components/Meta/og-image-url';
import { ReorderVersionsModal } from '~/components/Modals/ReorderVersionsModal';
import { ToggleLockModel } from '~/components/Model/Actions/ToggleLockModel';
import { ToggleLockModelComments } from '~/components/Model/Actions/ToggleLockModelComments';
import { ToggleMinorModel } from '~/components/Model/Actions/ToggleMinorModel';
import { ToggleSfwOnlyModel } from '~/components/Model/Actions/ToggleSfwOnlyModel';
import { HowToButton } from '~/components/Model/HowToUseModel/HowToUseModel';
import { ModelUnpublishedAlert } from '~/components/Model/ModelUnpublishedAlert';
import { siteAppealHref } from '~/components/SiteAppeal/site-appeal-href';
import { HIDDEN_METRIC_MESSAGE, HiddenMetricNotice } from '~/components/Model/HiddenMetricNotice';
import { ModelMinorFlagAlert } from '~/components/Model/ModelMinorFlagAlert';
import { ModelVersionList } from '~/components/Model/ModelVersionList/ModelVersionList';
import {
  CategoryBadge,
  ModelStatBadge,
  ModelStatText,
  ModelTitle,
  PageGroup,
  PageStack,
  TagBadge,
  TagDivider,
  UpdatedText,
} from '~/components/Model/ModelPageDs/ModelPageDs';
import {
  ComCardPreviewToggle,
  RedCardLink,
} from '~/components/Model/ComCardPreview/ComCardPreview';
import {
  canLinkToRedCard,
  canPreviewComCard,
  getCardViewer,
  withComCard,
} from '~/components/Model/ComCardPreview/com-card-preview';
import {
  modelVersionAccessInput,
  toModelVersionPermissionData,
  useModelVersionPermission,
} from '~/components/Model/ModelVersions/model-version.utils';
import { ModelVersionDetails } from '~/components/Model/ModelVersions/ModelVersionDetails';
import { FirstPublishCard } from '~/components/CreatorJourney/FirstPublishCard';
import { NextLink as Link } from '~/components/NextLink/NextLink';
import { PageLoader } from '~/components/PageLoader/PageLoader';
import { AddToShowcaseMenuItem } from '~/components/Profile/AddToShowcaseMenuItem';
import { AddEventDecorationMenuItem } from '~/components/Decorations/AddEventDecorationMenuItem';
import { useToggleFavoriteMutation } from '~/components/ResourceReview/resourceReview.utils';
import { GenerateButton } from '~/components/RunStrategy/GenerateButton';
import { ThumbsUpIcon } from '~/components/ThumbsIcon/ThumbsIcon';
import { useTourContext } from '~/components/Tours/ToursProvider';
import { TrackView } from '~/components/TrackView/TrackView';
import { env } from '~/env/client';
import { moderatorModelLookupPath } from '~/shared/constants/moderator-app';
import { useHiddenPreferencesData } from '~/hooks/hidden-preferences';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useEngagedModelMembership } from '~/hooks/useEngagedModelMembership';
import useIsClient from '~/hooks/useIsClient';
import { useBrowsingSettingsAddons } from '~/providers/BrowsingSettingsAddonsProvider';
import { useBrowsingSettings } from '~/providers/BrowserSettingsProvider';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import { CAROUSEL_LIMIT } from '~/server/common/constants';
import { ImageSort } from '~/server/common/enums';
import type { ModelMeta } from '~/server/schema/model.schema';
import { ReportEntity } from '~/shared/utils/report-helpers';
import { hasEntityAccess } from '~/server/services/common.service';
import { getDefaultModelVersion } from '~/server/services/model-version.service';
import { getServerBrowsingLevel, getServerImageQueryFilters } from '~/server/utils/browsing-level';
import { PAID_ACCESS_REFUND_WINDOW_DAYS } from '~/server/utils/early-access-helpers';
import { createServerSideProps } from '~/server/utils/server-side-helpers';
import {
  getIsSafeBrowsingLevel,
  publicBrowsingLevelsFlag,
  sfwBrowsingLevelsFlag,
} from '~/shared/constants/browsingLevel.constants';
import { ModelModifier } from '~/shared/utils/prisma/enums';
import {
  AppealCategory,
  Availability,
  CollectionType,
  CosmeticEntity,
  ModelStatus,
  ModelType,
  UserHubSourceType,
} from '~/shared/utils/prisma/enums';
import { formatDate, isFutureDate } from '~/utils/date-helpers';
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from '~/utils/notifications';
import { abbreviateNumber } from '~/utils/number-helpers';
import { buildPassthroughQuery } from '~/utils/query-string-helpers';
import {
  getDisplayName,
  getModelUrl,
  removeTags,
  slugit,
  splitUppercase,
} from '~/utils/string-helpers';
import { trpc } from '~/utils/trpc';
import { isNumber } from '~/utils/type-guards';

import classes from './[[...slug]].module.scss';
import { LegacyActionIcon } from '~/components/LegacyActionIcon/LegacyActionIcon';
import { ModelDiscussion } from '~/components/Model/Discussion/ModelDiscussion';
import { ModelGallery } from '~/components/Model/Gallery/ModelGallery';
import { getBaseModelSeoName } from '~/shared/constants/basemodel.constants';
import { AdUnitSide_1, AdUnitTop } from '~/components/Ads/AdUnit';
import { Meta } from '~/components/Meta/Meta';
import { ModeratorLookupMenuItem } from '~/components/Moderation/ModeratorLookupMenuItem';
import { TrainingAuditMenuItem } from '~/components/Moderation/TrainingAuditMenuItem';

// Keep in sync with $rail-activate in [[...slug]].module.scss.
const RAIL_ACTIVATE_WIDTH = 1440;

const NOT_FOUND = Symbol('model-not-found');

const isNotFoundError = (error: unknown) => {
  // `code` at the top level is a server-side `TRPCError`; `data.code` is a `TRPCClientError`.
  const err = error as { code?: string; data?: { code?: string } } | null | undefined;
  return err?.code === 'NOT_FOUND' || err?.data?.code === 'NOT_FOUND';
};

export const getServerSideProps = createServerSideProps({
  useSSG: true,
  useSession: true,
  resolver: async ({ ssg, ctx, session, features }) => {
    const params = (ctx.params ?? {}) as {
      id: string;
      slug: string[];
    };
    const query = ctx.query as {
      modelVersionId: string;
    };
    const id = Number(params.id);
    const modelVersionId = query.modelVersionId ? Number(query.modelVersionId) : undefined;
    if (!isNumber(id)) return { notFound: true };
    const version = await getDefaultModelVersion({
      modelId: id,
      modelVersionId,
      userId: session?.user?.id,
      isModerator: session?.user?.isModerator,
    }).catch(() => null);
    const modelVersionIdParsed = modelVersionId ?? version?.id;

    if (!modelVersionIdParsed && !session?.user?.isModerator) {
      return { notFound: true };
    }

    if (version?.model?.availability === Availability.Private) {
      // We'll do a explicit check if we know it's a private model
      if (!session?.user) {
        return {
          notFound: true,
        };
      }

      const [access] = await hasEntityAccess({
        entityIds: [version?.model.id],
        entityType: 'Model',
        userId: session.user.id,
        isModerator: session.user.isModerator,
      });

      if (!access.hasAccess) {
        return {
          notFound: true,
        };
      }
    }

    if (version?.availability === Availability.Private) {
      // We'll do a explicit check if we know it's a private model
      if (!session?.user) {
        return {
          notFound: true,
        };
      }

      const [access] = await hasEntityAccess({
        entityIds: [version.id],
        entityType: 'ModelVersion',
        userId: session.user.id,
        isModerator: session.user.isModerator,
      });

      if (!access.hasAccess) {
        return {
          notFound: true,
        };
      }
    }

    const isTraining = !!version?.trainingStatus;
    const draft = version?.status === 'Draft';
    const isOwner = version?.model.userId === session?.user?.id;
    // TODO: Commenting cause @ally found it's ideal to be able to enter a models' page even if it's unpublished.
    // if (isTraining && isOwner) {
    //   // Start checking whether to redirect:
    //   if (draft) {
    //     return {
    //       redirect: {
    //         destination: `/models/${version.model.id}/model-versions/${version.id}/wizard?step=1`,
    //         permanent: false,
    //       },
    //     };
    //   }
    // }

    let gating: { contentNsfwLevel: number; nsfw?: boolean } | undefined;
    let suppressAds = false;

    if (ssg) {
      // Fetch the model first so we can short-circuit on slug mismatch before
      // doing any other prefetch work. Stale links from search results /
      // bookmarks land here often; bailing early saves ~5 DB roundtrips.
      const fetched = await ssg.model.getById
        .fetch({ id, excludeTrainingData: true })
        .catch((error) => (isNotFoundError(error) ? NOT_FOUND : null));
      // `getById` is viewer-scoped, so this is a 404 for exactly the viewers it refuses; any
      // other failure still renders and lets the client retry.
      if (fetched === NOT_FOUND) return { notFound: true };
      const model = fetched;

      if (model) {
        gating = {
          contentNsfwLevel: model.nsfwLevel,
          // A complete .com face is what civitai.com shows; it is not the mature listing.
          nsfw: model.nsfw && !model.hasSfwFace,
        };
        suppressAds = model.status !== ModelStatus.Published;
      }

      // Redirect to canonical slug URL if slug is missing or incorrect
      if (model) {
        const correctSlug = slugit(model.name);
        const currentSlug = params.slug?.join('/');
        if (correctSlug && currentSlug !== correctSlug) {
          const queryString = buildPassthroughQuery(ctx.query);
          // 308 only for bare-id → slug canonical mapping with no query string.
          // Some browsers cache 308 by path alone and drop the query on
          // subsequent hits, which strands deep-link params (highlight=,
          // commentParentType=, etc.). 307 anywhere a query is involved or the
          // slug differs — request-specific, should not be cached.
          const permanent = !currentSlug && !queryString;
          return {
            redirect: {
              destination: `/models/${id}/${correctSlug}${queryString}`,
              permanent,
            },
          };
        }
      }

      // No redirect — fire the remaining prefetches in parallel. They have no
      // mutual dependencies, so SSR latency drops from sum(latencies) to
      // max(latency).
      await Promise.all(
        [
          ssg.hiddenPreferences.getHidden.prefetch(),
          modelVersionIdParsed
            ? ssg.common.getEntityAccess.prefetch(
                modelVersionAccessInput(modelVersionIdParsed as number)
              )
            : null,
          ssg.model.getCollectionShowcase.prefetch({ id }),
          // Creator-card CLS fix: SSR-prefetch user.getCreator (the SmartCreatorCard
          // query, keyed by `{ id }`) so it hydrates with the real creator — stats,
          // rank, links, badges — instead of rendering the zeroed fallback and
          // growing once the client fetch lands. Simple key, no middleware to
          // mismatch; fail-soft on miss.
          // Kept under `modelPageDeferBelowFold` too: there the card renders client-side only
          // (see ModelVersionDetails), and this prefetch lets it mount from the dehydrated
          // cache with the real creator, so the page shifts once (placeholder → card) rather
          // than twice (placeholder → zeroed fallback → card).
          model?.user?.id
            ? ssg.user.getCreator.prefetch({ id: model.user.id }).catch(() => null)
            : null,
          session ? ssg.user.getEngagedModelVersions.prefetch({ id }) : null,
          session ? ssg.resourceReview.getUserResourceReview.prefetch({ modelId: id }) : null,
          // App Blocks CLS fix: SSR-prefetch the slot's listForModel query so
          // the client `useBlockSlot` useQuery hydrates with isLoading already
          // false on first render — the slot reserves the right height from
          // real data instead of flashing 0px → full frame (layout shift).
          // The input MUST match useBlockSlot's exactly (slotId, modelId,
          // modelType, modelNsfwLevel) or the React Query cache keys won't
          // line up and hydration won't apply. Cheap: the blocks router gates
          // on the appBlocks flag and returns [] when dark, and listForModel
          // itself is the 60s-cached, indexed path. Fail-soft — a prefetch
          // miss just falls back to the (now reserved-height) loading path.
          model
            ? ssg.blocks.listForModel
                .prefetch({
                  slotId: 'model.sidebar_top',
                  modelId: id,
                  modelType: model.type,
                  modelNsfwLevel: model.nsfwLevel ?? undefined,
                })
                .catch(() => null)
            : null,
          // Carousel CLS fix: SSR-prefetch the images query the ModelCarousel runs
          // client-side, so it hydrates with isLoading already false — the carousel
          // reserves the right height from real image dimensions instead of a fixed
          // 600px placeholder that rarely matches (layout shift). The input MUST
          // match useQueryImages exactly — browsingLevel and the addon-derived
          // excludedTagIds/disablePoi/disableMinor all key the query — or the React
          // Query key won't line up and the client refetches. Fail-soft on any miss.
          model && modelVersionIdParsed
            ? (async () => {
                // Mirror ModelCarousel: a minor model forces its carousel to SFW for
                // non-moderators, so SSR must resolve at that same forced level. Using
                // the viewer's own level here would key on a different disableMinor
                // (and browsingLevel) than the client, orphaning the prefetch on
                // exactly the models this matters for.
                const forceMinorLevel = !!model.minor && !session?.user?.isModerator;
                const browsingLevel = forceMinorLevel
                  ? session?.user
                    ? sfwBrowsingLevelsFlag
                    : publicBrowsingLevelsFlag
                  : getServerBrowsingLevel({
                      canViewNsfw: features?.canViewNsfw ?? false,
                      user: session?.user,
                    });
                const filters = await getServerImageQueryFilters(browsingLevel, {
                  isModerator: session?.user?.isModerator,
                });
                if (!filters) return null;
                return ssg.image.getInfinite
                  .prefetchInfinite({
                    modelVersionId: modelVersionIdParsed as number,
                    prioritizedUserIds: [model.user.id],
                    period: 'AllTime',
                    sort: ImageSort.MostReactions,
                    limit: CAROUSEL_LIMIT,
                    pending: true,
                    include: [],
                    withMeta: false,
                    ...filters,
                  })
                  .catch(() => null);
              })()
            : null,
        ].filter(Boolean)
      );
    }

    return {
      props: { id },
      gating,
      suppressAds,
    };
  },
});

export default function ModelDetailsV2({
  id,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const currentUser = useCurrentUser();

  const router = useRouter();
  const colorScheme = useComputedColorScheme('dark');
  const queryUtils = trpc.useUtils();
  const isClient = useIsClient();
  const features = useFeatureFlags();
  const ds = !!features.modelPageDesignSystemMarkup;
  const { activeTour, running, runTour } = useTourContext();

  const [opened, { toggle }] = useDisclosure();
  const canShowRail = useMediaQuery(`(min-width: ${RAIL_ACTIVATE_WIDTH}px)`, false);
  const allowAds = useBrowsingSettings((x) => x.allowAds);

  const { blockedUsers } = useHiddenPreferencesData();

  const { data: storedModel, isLoading: loadingModel } = trpc.model.getById.useQuery({
    id,
    excludeTrainingData: true,
  });
  const cardViewer = getCardViewer(currentUser, storedModel?.user.id, !!features.isGreen);
  // Keyed on the model id: the page stays mounted across in-app links to another model.
  const [previewedModelId, setPreviewedModelId] = useState<number>();
  const previewComCard = previewedModelId === id;
  const setPreviewComCard = (on: boolean) => setPreviewedModelId(on ? id : undefined);
  const showComCardToggle = canPreviewComCard(cardViewer, storedModel);
  const showRedCardLink = canLinkToRedCard(cardViewer, storedModel?.sfwName);
  const model = useMemo(
    () =>
      storedModel && showComCardToggle && previewComCard ? withComCard(storedModel) : storedModel,
    [storedModel, showComCardToggle, previewComCard]
  );
  const browsingSettingsAddons = useBrowsingSettingsAddons();

  const rawVersionId = router.query.modelVersionId;
  const modelVersionId = Number(
    (Array.isArray(rawVersionId) ? rawVersionId[0] : rawVersionId) ?? model?.modelVersions[0]?.id
  );

  // PR2: per-visible-set membership for just this model (was the unbounded getEngagedModels).
  const { isEngaged: isModelEngaged, isKnown: isFavoriteKnown } = useEngagedModelMembership(
    model?.id ?? 0
  );
  const isFavorite = !!model && isModelEngaged('Recommended');

  const isModerator = currentUser?.isModerator ?? false;
  const isCreator = model?.user.id === currentUser?.id;
  const isOwner = isCreator || isModerator;
  const publishedVersions = useMemo(() => {
    return !isOwner
      ? model?.modelVersions.filter((v) => v.status === ModelStatus.Published) ?? []
      : model?.modelVersions ?? [];
  }, [isOwner, model?.modelVersions]);
  // The querystring is the source of truth for which version is shown. Deriving it
  // (rather than mirroring it into state) is what keeps an in-page link to a sibling
  // version from being snapped back by a sync effect.
  const selectedVersion =
    publishedVersions.find((version) => version.id === modelVersionId) ??
    publishedVersions[0] ??
    null;
  const selectedEcosystemName = getBaseModelSeoName(selectedVersion?.baseModel);
  const tippedAmount = useBuzzTippingStore({ entityType: 'Model', entityId: model?.id ?? -1 });
  const buzzEarned =
    tippedAmount +
    (model?.rank?.tippedAmountCountAllTime ?? 0) +
    (model?.modelVersions?.reduce(
      (acc, version) => acc + (version.rank?.earnedAmountAllTime ?? 0),
      0
    ) ?? 0);

  const { canDownload: hasDownloadPermissions, canGenerate: hasGeneratePermissions } =
    useModelVersionPermission({
      modelVersionId: selectedVersion?.id,
      // The version is already on hand from model.getById — don't refetch it via modelVersion.getById.
      modelVersion:
        model && selectedVersion ? toModelVersionPermissionData(model, selectedVersion) : undefined,
    });

  const latestGenerationVersion = publishedVersions.find((version) => version.canGenerate);

  // TODO change this to just grab one image, since that's all it's used for
  const { images: versionImages, isLoading: loadingImages } = useQueryImages(
    {
      modelVersionId: selectedVersion?.id,
      prioritizedUserIds: model ? [model.user.id] : undefined,
      period: 'AllTime',
      sort: ImageSort.MostReactions,
      limit: CAROUSEL_LIMIT,
      pending: true,
      include: [],
      withMeta: false,
    },
    { enabled: !!selectedVersion }
  );

  const deleteMutation = trpc.model.delete.useMutation({
    async onSuccess(_, { permanently }) {
      await queryUtils.model.getAll.invalidate();
      if (!permanently) await queryUtils.model.getById.invalidate({ id });
      if (!isModerator || permanently) await router.replace('/models');

      showSuccessNotification({
        title: 'Successfully deleted the model',
        message: 'Your model has been deleted',
      });
      closeAllModals();
    },
    onError(error) {
      showErrorNotification({
        error: new Error(error.message),
        title: 'Could not delete model',
        reason: error.message ?? 'An unexpected error occurred, please try again',
      });
    },
  });
  const handleDeleteModel = (options?: { permanently: boolean }) => {
    const { permanently = false } = options || {};

    openConfirmModal({
      title: 'Delete Model',
      children: permanently
        ? 'Are you sure you want to permanently delete this model? This action is destructive and cannot be reverted.'
        : 'Are you sure you want to delete this model? This action is destructive and you will have to contact support to restore your data.',
      centered: true,
      labels: { confirm: 'Delete Model', cancel: "No, don't delete it" },
      confirmProps: { color: 'red', disabled: deleteMutation.isPending },
      closeOnConfirm: false,
      onConfirm: () => {
        if (model) {
          deleteMutation.mutate({ id: model.id, permanently });
        }
      },
    });
  };

  const unpublishModelMutation = trpc.model.unpublish.useMutation({
    async onSuccess() {
      await queryUtils.model.getById.invalidate({ id });
    },
    onError(error) {
      showErrorNotification({ error: new Error(error.message) });
    },
  });
  const handleUnpublishModel = async () => {
    try {
      // The client's global default is `staleTime: Infinity` (`src/utils/trpc.ts`), and `fetchQuery`
      // applies it — so without this the dialog can show a Buzz figure cached from an earlier open.
      const refund = await queryUtils.model.getEarlyAccessRefundRequirement.fetch(
        { id },
        { staleTime: 0 }
      );
      const exemptNote =
        refund.exemptBuyerCount > 0
          ? ` ${refund.exemptBuyerCount} earlier buyer(s) bought more than ${PAID_ACCESS_REFUND_WINDOW_DAYS} days ago; they lose access and are not refunded.`
          : '';
      if (refund.purchaseCount > 0) {
        openConfirmModal({
          title: 'Refund early access buyers',
          children: `${
            refund.buyerCount
          } member(s) bought early access to this model in the last ${PAID_ACCESS_REFUND_WINDOW_DAYS} days. Unpublishing it now will refund them a total of ${refund.totalBuzz.toLocaleString()} Buzz from your account and revoke their early access.${exemptNote} Do you want to continue?`,
          centered: true,
          labels: { confirm: 'Refund & Unpublish', cancel: 'Cancel' },
          confirmProps: { color: 'yellow' },
          onConfirm: () => unpublishModelMutation.mutate({ id, refundEarlyAccess: true }),
        });
      } else if (refund.exemptBuyerCount > 0) {
        openConfirmModal({
          title: 'Unpublish model',
          children: `${refund.exemptBuyerCount} member(s) bought early access to this model, all more than ${PAID_ACCESS_REFUND_WINDOW_DAYS} days ago. They lose access to the model and are not refunded, and nothing is taken from your account. Do you want to continue?`,
          centered: true,
          labels: { confirm: 'Unpublish', cancel: 'Cancel' },
          onConfirm: () => unpublishModelMutation.mutate({ id }),
        });
      } else {
        unpublishModelMutation.mutate({ id });
      }
    } catch (error) {
      showErrorNotification({
        error: error instanceof Error ? error : new Error('Could not check early access purchases'),
      });
    }
  };
  const publishModelMutation = trpc.model.publish.useMutation({
    async onSuccess(result) {
      notifyIfPublishHeld(result);
      await queryUtils.model.getById.invalidate({ id });
    },
    onError(error) {
      showErrorNotification({ error: new Error(error.message) });
    },
  });
  const restoreModelMutation = trpc.model.restore.useMutation({
    async onSuccess() {
      await queryUtils.model.getById.invalidate({ id });
      await queryUtils.model.getAll.invalidate();
    },
    onError(error) {
      showErrorNotification({ error: new Error(error.message) });
    },
  });

  const handleCollect = () => {
    openAddToCollectionModal({
      props: {
        modelId: id,
        type: CollectionType.Model,
      },
    });
  };

  const changeModeMutation = trpc.model.changeMode.useMutation();
  const handleChangeMode = async (mode: ModelModifier | null) => {
    const prevModel = queryUtils.model.getById.getData({ id });
    await queryUtils.model.getById.cancel({ id });

    if (prevModel)
      queryUtils.model.getById.setData(
        { id },
        { ...prevModel, mode, meta: (prevModel.meta as ModelMeta) ?? null }
      );

    changeModeMutation.mutate(
      { id, mode },
      {
        async onSuccess() {
          await queryUtils.model.getById.invalidate({ id });
        },
        onError(error) {
          showErrorNotification({
            title: 'Unable to change mode',
            error: new Error(error.message),
          });
          queryUtils.model.getById.setData({ id }, prevModel);
        },
      }
    );
  };

  const rescanModelMutation = trpc.model.rescan.useMutation({
    onSuccess(data) {
      const { sent = 0, failed = 0 } = (data ?? {}) as { sent?: number; failed?: number };
      if (sent === 0 && failed > 0) {
        showErrorNotification({
          error: new Error(
            `All ${failed} file(s) failed to send for scanning. Please try again later.`
          ),
          title: 'Rescan failed',
        });
      } else if (failed > 0) {
        showWarningNotification({
          title: 'Rescan partially failed',
          message: `Sent ${sent} file(s) for scanning, ${failed} could not be processed.`,
        });
      } else {
        showSuccessNotification({
          title: 'Rescan requested',
          message: `${sent} file(s) sent for scanning.`,
        });
      }
    },
    onError(error) {
      showErrorNotification({ error, title: 'Failed to rescan files' });
    },
  });
  const handleRescanModel = async () => {
    rescanModelMutation.mutate({ id });
  };

  const favoriteMutation = useToggleFavoriteMutation();
  const handleToggleFavorite = ({ versionId, setTo }: { versionId?: number; setTo: boolean }) => {
    if (!model) return;
    favoriteMutation.mutate({
      modelId: model.id,
      modelVersionId: versionId,
      setTo,
    });
  };

  const handlePublishModel = () => {
    if (!model) return;

    const isUnpublished = model.status === ModelStatus.Unpublished;
    const isUnpublishedViolation = model.status === ModelStatus.UnpublishedViolation;
    const canRepublish =
      (isUnpublished && (isCreator || isModerator)) || (isUnpublishedViolation && isModerator);

    if (!canRepublish) return;

    const isPrivate = model.availability === Availability.Private;
    const message = isPrivate
      ? 'This model and all of its versions will be restored to their previous state while remaining private. Are you sure you want to republish this model?'
      : 'This model and all of its versions will be publicly available. Are you sure you want to republish this model?';

    openConfirmModal({
      centered: true,
      closeOnConfirm: false,
      title: 'Republish model',
      children: message,
      labels: { confirm: 'Yes, republish', cancel: 'No, go back' },
      onConfirm: () => {
        publishModelMutation.mutate(
          {
            id: model.id,
            versionIds: model?.modelVersions.map((v) => v.id),
          },
          { onSuccess: () => closeAllModals() }
        );
      },
    });
  };

  const toggleCannotPromoteMutation = trpc.model.toggleCannotPromote.useMutation({
    async onSuccess({ id, meta }) {
      const prevModel = queryUtils.model.getById.getData({ id });
      await queryUtils.model.getById.cancel({ id });

      if (prevModel) {
        queryUtils.model.getById.setData({ id }, { ...prevModel, meta });
      }

      // invalidate all auction results in case we deleted bids
      await queryUtils.auction.getBySlug.invalidate();

      showSuccessNotification({ message: 'Successfully toggled cannot promote' });
    },
    onError(error) {
      showErrorNotification({ title: 'Failed to toggle', error: new Error(error.message) });
    },
  });
  const handleToggleCannotPromote = () => {
    toggleCannotPromoteMutation.mutate({ id });
  };

  const setOfficialMutation = trpc.model.setOfficial.useMutation({
    async onSuccess({ id, isOfficial }) {
      const prevModel = queryUtils.model.getById.getData({ id });
      await queryUtils.model.getById.cancel({ id });
      if (prevModel) {
        queryUtils.model.getById.setData({ id }, { ...prevModel, isOfficial });
      }
      showSuccessNotification({
        message: isOfficial ? 'Marked as official' : 'Unmarked as official',
      });
    },
    onError(error) {
      showErrorNotification({ title: 'Failed to update', error: new Error(error.message) });
    },
  });
  const handleSetOfficial = (isOfficial: boolean) => {
    setOfficialMutation.mutate({ id, isOfficial });
  };

  const view = router.query.view;
  const basicView = view === 'basic' && isModerator;
  const canLoadBelowTheFold = isClient && !loadingModel && !loadingImages && !basicView;

  // Pin the resolved version into the url so shares/refreshes land on what's displayed.
  // One-way (state never drives this back): the url already decides the selection.
  useEffect(() => {
    if (loadingModel || !selectedVersion || selectedVersion.id === modelVersionId) return;
    router.replace(
      getModelUrl({
        modelId: id,
        modelName: storedModel?.name,
        modelVersionId: selectedVersion.id,
      }),
      undefined,
      { shallow: true }
    );
  }, [id, storedModel?.name, selectedVersion, modelVersionId, loadingModel, router]);

  useEffect(() => {
    if (!canLoadBelowTheFold) return;
    if ((activeTour === 'model-page' || activeTour === 'welcome') && !running)
      runTour({ key: activeTour });
    // only run when the model is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadBelowTheFold]);

  if (loadingModel) return <PageLoader />;

  // Handle missing and deleted models
  const modelDoesntExist = !model;
  const modelDeleted = !!model && !!model.deletedAt && model.status === ModelStatus.Deleted;
  const modelNotVisible =
    model &&
    !isOwner &&
    !isModerator &&
    // Check if published or has any model versions
    (model.status !== ModelStatus.Published || !model.modelVersions.length);

  const isBlocked = blockedUsers.find((u) => u.id === model?.user.id);

  if (modelDeleted && !isOwner && !isModerator)
    return (
      <>
        <Meta title="This resource has been removed by its owner" deIndex />
        <Center p="xl">
          <Alert>
            <Text size="lg">This resource has been removed by its owner</Text>
          </Alert>
        </Center>
      </>
    );

  if (modelDoesntExist || ((modelDeleted || modelNotVisible || isBlocked) && !isModerator)) {
    return <NotFound />;
  }

  if (
    model.poi &&
    browsingSettingsAddons.settings.disablePoi &&
    model.user.id !== currentUser?.id
  ) {
    return <NotFound />;
  }

  const image = versionImages.find((image) => getIsSafeBrowsingLevel(image.nsfwLevel));
  const imageUrl = image ? getEdgeUrl(image.url, { width: 1200 }) : undefined;
  const thumbsUpCount = model.rank?.thumbsUpCountAllTime ?? 0;
  const thumbsDownCount = model.rank?.thumbsDownCountAllTime ?? 0;
  const totalRatingCount = thumbsUpCount + thumbsDownCount;
  const metaSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    applicationCategory: 'Multimedia',
    applicationSubCategory: `${selectedEcosystemName} Model`,
    description: removeTags(model.description ?? ''),
    name: model.name,
    image: imageUrl,
    // Google rejects an aggregateRating whose item has no author, so deleted users get a placeholder.
    author:
      !model.user.deletedAt && model.user.username
        ? {
            '@type': 'Person',
            name: model.user.username,
            url: env.NEXT_PUBLIC_BASE_URL
              ? `${env.NEXT_PUBLIC_BASE_URL}/user/${model.user.username}`
              : undefined,
          }
        : { '@type': 'Person', name: 'Civitai user' },
    datePublished: model.publishedAt,
    ...(totalRatingCount > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: Math.max(Math.min(Math.ceil((thumbsUpCount / totalRatingCount) * 5), 5), 1),
        reviewCount: totalRatingCount,
        bestRating: 5,
        worstRating: 1,
      },
    }),
  };

  const published = model.status === ModelStatus.Published;
  const inaccurate = model.modelVersions.some((version) => version.inaccurate);
  const isMuted = currentUser?.muted ?? false;
  const onlyEarlyAccess = model.modelVersions.every((version) => version.earlyAccessDeadline);
  const canDiscuss =
    features.canWrite &&
    !isMuted &&
    (!onlyEarlyAccess ||
      hasDownloadPermissions ||
      hasGeneratePermissions ||
      currentUser?.isModerator);
  const versionCount = model.modelVersions.length;
  const inEarlyAccess = model.modelVersions.some(
    (v) => !!v.earlyAccessDeadline && isFutureDate(v.earlyAccessDeadline)
  );
  const versionIsEarlyAccess =
    selectedVersion &&
    !!selectedVersion.earlyAccessDeadline &&
    isFutureDate(selectedVersion.earlyAccessDeadline);
  const category = model.tagsOnModels.find(({ tag }) => !!tag.isCategory)?.tag;
  const tags = model.tagsOnModels.filter(({ tag }) => !tag.isCategory).map((tag) => tag.tag);
  const isBannedFromPromotion = model.meta?.cannotPromote ?? false;
  const adsDisabledByUser = (currentUser?.isMember ?? false) && !allowAds;
  const showRail = !model.poi && !adsDisabledByUser;

  return (
    <Gated
      contentNsfwLevel={model.nsfwLevel}
      nsfw={model.nsfw && !model.hasSfwFace}
      bypassRating={isOwner}
      suppressAds={!published}
      meta={{
        title: `${model.name}${
          selectedVersion ? ' - ' + selectedVersion.name : ''
        } | ${selectedEcosystemName} ${getDisplayName(model.type)} | Civitai`,
        description: truncate(removeTags(model.description ?? ''), { length: 150 }),
        images: versionImages,
        ogEndpoint: modelOgEndpoint(model),
        canonical: `/models/${model.id}/${slugit(model.name)}`,
        alternate: `/models/${model.id}`,
        schema: metaSchema,
        breadcrumb: buildBreadcrumbSchema(env.NEXT_PUBLIC_BASE_URL ?? '', [
          { name: 'Models', path: '/models' },
          ...(category
            ? [{ name: category.name, path: `/tag/${encodeURIComponent(category.name)}` }]
            : []),
          { name: model.name },
        ]),
        deIndex:
          model.status !== ModelStatus.Published ||
          model.availability === Availability.Unsearchable,
      }}
    >
      <TrackView entityId={model.id} entityType="Model" type="ModelView" />
      <RenderAdUnitOutstream minContainerWidth={2800} />
      <div className={showRail ? `${classes.mainRegion} ${classes.withRail}` : classes.mainRegion}>
        <Container size="xl" px={0} className={classes.contentCol} data-tour="model:start">
          <PageStack ds={ds} gap="xl">
            <PageStack ds={ds} gap="xs">
              {showComCardToggle && (
                <ComCardPreviewToggle checked={previewComCard} onChange={setPreviewComCard} />
              )}
              {showRedCardLink && <RedCardLink path={router.asPath} />}
              <PageStack ds={ds} gap={4}>
                <PageGroup ds={ds} align="flex-start" justify="space-between" nowrap>
                  <PageGroup ds={ds} className={classes.titleWrapper}>
                    <ModelTitle ds={ds} className={classes.title}>
                      {model?.name}
                    </ModelTitle>
                    <StatHoverCard
                      label="Unique Reviews"
                      value={model.rank?.thumbsUpCountAllTime ?? 0}
                    >
                      <div>
                        <LoginRedirect reason="favorite-model">
                          <ModelStatBadge
                            ds={ds}
                            color={isFavorite ? 'green' : 'gray'}
                            icon={
                              <ThumbsUpIcon
                                size={18}
                                color={isFavorite ? 'green' : undefined}
                                filled={isFavorite}
                              />
                            }
                            className={isFavoriteKnown ? 'cursor-pointer' : 'cursor-progress'}
                            onClick={() => {
                              // F1: don't toggle until Recommended membership is known —
                              // a cold store reads not-favorited and would flip intent.
                              if (!isFavoriteKnown) return;
                              handleToggleFavorite({ setTo: !isFavorite });
                            }}
                          >
                            <ModelStatText ds={ds} className={classes.modelBadgeText}>
                              {abbreviateNumber(model.rank?.thumbsUpCountAllTime ?? 0)}
                            </ModelStatText>
                          </ModelStatBadge>
                        </LoginRedirect>
                      </div>
                    </StatHoverCard>
                    <StatHoverCard
                      label="Unique Downloads"
                      value={model.rank?.downloadCountAllTime ?? 0}
                      message={model.hiddenMetrics?.downloads ? HIDDEN_METRIC_MESSAGE : undefined}
                    >
                      <ModelStatBadge ds={ds} icon={<IconDownload size={18} />}>
                        <ModelStatText ds={ds} className={classes.modelBadgeText}>
                          {model.hiddenMetrics?.downloads ? (
                            <HiddenMetricNotice size={18} withTooltip={false} />
                          ) : (
                            abbreviateNumber(model.rank?.downloadCountAllTime ?? 0)
                          )}
                        </ModelStatText>
                      </ModelStatBadge>
                    </StatHoverCard>
                    {latestGenerationVersion && (
                      <GenerateButton
                        versionId={latestGenerationVersion.id}
                        modelId={model.id}
                        data-activity="create:model-stat"
                      >
                        <ModelStatBadge ds={ds} icon={<IconBrush size={18} />}>
                          <ModelStatText ds={ds} className={classes.modelBadgeText}>
                            {model.hiddenMetrics?.generations ? (
                              <HiddenMetricNotice size={18} />
                            ) : (
                              abbreviateNumber(model.rank?.generationCountAllTime ?? 0)
                            )}
                          </ModelStatText>
                        </ModelStatBadge>
                      </GenerateButton>
                    )}
                    {features.collections && (
                      <StatHoverCard
                        label="Collections"
                        value={model.rank?.collectedCountAllTime ?? 0}
                      >
                        <div>
                          <LoginRedirect reason="add-to-collection">
                            <ModelStatBadge
                              ds={ds}
                              icon={<IconBookmark size={18} />}
                              className="cursor-pointer"
                              onClick={handleCollect}
                            >
                              <ModelStatText ds={ds} className={classes.modelBadgeText}>
                                {abbreviateNumber(model.rank?.collectedCountAllTime ?? 0)}
                              </ModelStatText>
                            </ModelStatBadge>
                          </LoginRedirect>
                        </div>
                      </StatHoverCard>
                    )}
                    {!model.poi && (
                      <StatHoverCard
                        label="Buzz Earned"
                        value={buzzEarned}
                        message={model.hiddenMetrics?.buzz ? HIDDEN_METRIC_MESSAGE : undefined}
                      >
                        <div>
                          <InteractiveTipBuzzButton
                            toUserId={model.user.id}
                            entityId={model.id}
                            entityType="Model"
                          >
                            <ModelStatBadge
                              ds={ds}
                              className="cursor-pointer"
                              icon={
                                <IconBolt size={18} className="text-yellow-7" fill="currentColor" />
                              }
                            >
                              <ModelStatText ds={ds} className={classes.modelBadgeText}>
                                {model.hiddenMetrics?.buzz ? (
                                  <HiddenMetricNotice size={18} withTooltip={false} />
                                ) : (
                                  abbreviateNumber(buzzEarned)
                                )}
                              </ModelStatText>
                            </ModelStatBadge>
                          </InteractiveTipBuzzButton>
                        </div>
                      </StatHoverCard>
                    )}
                    {inEarlyAccess && (
                      <Tooltip
                        label={
                          <Text>
                            Early Access helps creators monetize,{' '}
                            <Anchor href="/articles/6341">learn more here</Anchor>
                          </Text>
                        }
                      >
                        <ModelStatBadge ds={ds} color="green" icon={<IconClock size={18} />}>
                          Early Access
                        </ModelStatBadge>
                      </Tooltip>
                    )}
                  </PageGroup>

                  <PageGroup ds={ds} gap={8} nowrap>
                    <HowToButton
                      size={30}
                      stroke={1.5}
                      href="https://education.civitai.com/civitais-guide-to-resource-types/#models"
                      tooltip="What is this?"
                    />
                    {features.appTour && (
                      <HelpButton
                        size="xl"
                        tooltip="Need help? Start the tour!"
                        iconProps={{ size: 30, stroke: 1.5 }}
                        onClick={() =>
                          runTour({
                            key: activeTour === 'welcome' ? 'welcome' : 'model-page',
                            step: 0,
                            forceRun: true,
                            trigger: 'help',
                          })
                        }
                      />
                    )}
                    <Menu
                      position="bottom-end"
                      transitionProps={{ transition: 'pop-top-right' }}
                      withinPortal
                    >
                      <Menu.Target>
                        <LegacyActionIcon
                          className={classes.headerButton}
                          variant="light"
                          aria-label="Model options"
                        >
                          <IconDotsVertical size={20} />
                        </LegacyActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown className="max-h-[70vh] overflow-y-auto">
                        {currentUser && isCreator && published && (
                          <Menu.Item
                            leftSection={<IconBan size={14} stroke={1.5} />}
                            color="yellow"
                            onClick={handleUnpublishModel}
                            disabled={unpublishModelMutation.isPending}
                          >
                            Unpublish
                          </Menu.Item>
                        )}
                        {currentUser &&
                          ((model.status === ModelStatus.Unpublished &&
                            (isCreator || isModerator)) ||
                            (model.status === ModelStatus.UnpublishedViolation && isModerator)) && (
                            <Menu.Item
                              leftSection={<IconRepeat size={14} stroke={1.5} />}
                              color="green"
                              onClick={handlePublishModel}
                              disabled={publishModelMutation.isPending}
                            >
                              Republish
                            </Menu.Item>
                          )}
                        {currentUser && isOwner && !modelDeleted && (
                          <>
                            <Menu.Item
                              color="red.6"
                              leftSection={<IconTrash size={14} stroke={1.5} />}
                              onClick={() => handleDeleteModel()}
                            >
                              Delete Model
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconEdit size={14} stroke={1.5} />}
                              component={Link}
                              href={`/models/${model.id}/edit`}
                            >
                              Edit Model
                            </Menu.Item>
                          </>
                        )}
                        {isCreator && published && features.creatorPromotions && (
                          <Menu.Item
                            leftSection={<IconSpeakerphone size={14} stroke={1.5} />}
                            onClick={() =>
                              dialogStore.trigger({
                                component: ModelPromotionModal,
                                props: { modelId: model.id },
                              })
                            }
                          >
                            Promote this model
                          </Menu.Item>
                        )}
                        {features.collections && (
                          <AddToCollectionMenuItem
                            onClick={() =>
                              openAddToCollectionModal({
                                props: {
                                  modelId: model.id,
                                  type: CollectionType.Model,
                                },
                              })
                            }
                          />
                        )}
                        <AddToHubMenuItem
                          onClick={() =>
                            openAddToHubModal({
                              props: {
                                source: {
                                  type: UserHubSourceType.Model,
                                  targetId: model.id,
                                  alias: storedModel?.name ?? model.name,
                                },
                              },
                            })
                          }
                        />
                        {isOwner && (
                          <AddToShowcaseMenuItem
                            key="add-to-showcase"
                            entityType="Model"
                            entityId={model.id}
                          />
                        )}
                        {/* The model itself, so its showcase images are not the nearest thing to hat. */}
                        {isCreator && versionImages[0] && (
                          <AddEventDecorationMenuItem
                            entityType={CosmeticEntity.Model}
                            entityId={model.id}
                            image={versionImages[0]}
                            findWornHat
                          />
                        )}
                        {(!currentUser || !isOwner || isModerator) && (
                          <LoginRedirect reason="report-model">
                            <Menu.Item
                              leftSection={<IconFlag size={14} stroke={1.5} />}
                              onClick={() =>
                                openReportModal({
                                  entityType: ReportEntity.Model,
                                  entityId: model.id,
                                })
                              }
                            >
                              Report
                            </Menu.Item>
                          </LoginRedirect>
                        )}
                        <RequestLockReviewMenuItem model={model} />
                        {currentUser && (
                          <>
                            <Menu.Label>Moderation</Menu.Label>
                            <HideUserButton as="menu-item" userId={model.user.id} />
                            <BlockUserButton as="menu-item" userId={model.user.id} />
                            <HideModelButton as="menu-item" model={model} />
                            <Menu.Item
                              leftSection={<IconTagOff size={14} stroke={1.5} />}
                              onClick={() =>
                                openBlockModelTagsModal({ props: { modelId: model.id } })
                              }
                            >
                              Hide content with these tags
                            </Menu.Item>
                            {isModerator && (
                              <>
                                <ModeratorLookupMenuItem
                                  path={moderatorModelLookupPath(model.id, selectedVersion?.id)}
                                >
                                  Lookup Model
                                </ModeratorLookupMenuItem>
                                <TrainingAuditMenuItem
                                  modelId={model.id}
                                  uploadType={model.uploadType}
                                  isModerator={isModerator}
                                />
                                {published && (
                                  <Menu.Item
                                    color="yellow"
                                    leftSection={<IconBan size={14} stroke={1.5} />}
                                    onClick={() =>
                                      openUnpublishModal({ props: { modelId: model.id } })
                                    }
                                  >
                                    Unpublish as Violation
                                  </Menu.Item>
                                )}
                                <Menu.Item
                                  color="orange"
                                  leftSection={<IconBan size={14} stroke={1.5} />}
                                  onClick={() => handleToggleCannotPromote()}
                                >
                                  {isBannedFromPromotion ? 'Allow Promoting' : 'Ban Promoting'}
                                </Menu.Item>
                                <Menu.Item
                                  color="blue"
                                  leftSection={<IconRosetteDiscountCheck size={14} stroke={1.5} />}
                                  onClick={() => handleSetOfficial(!model.isOfficial)}
                                >
                                  {model.isOfficial ? 'Unmark Official' : 'Mark Official'}
                                </Menu.Item>
                                <Menu.Item
                                  color="red.6"
                                  leftSection={<IconTrash size={14} stroke={1.5} />}
                                  onClick={() => handleDeleteModel({ permanently: true })}
                                >
                                  Permanently Delete Model
                                </Menu.Item>
                                {modelDeleted && (
                                  <Menu.Item
                                    leftSection={<IconRecycle size={14} stroke={1.5} />}
                                    color="green"
                                    onClick={() => restoreModelMutation.mutate({ id })}
                                    disabled={restoreModelMutation.isPending}
                                  >
                                    Restore
                                  </Menu.Item>
                                )}
                                <Menu.Item
                                  leftSection={
                                    rescanModelMutation.isPending ? (
                                      <Loader size={14} />
                                    ) : (
                                      <IconRadar2 size={14} stroke={1.5} />
                                    )
                                  }
                                  onClick={() => handleRescanModel()}
                                >
                                  Rescan Files
                                </Menu.Item>
                                <ToggleLockModel modelId={model.id} locked={model.locked}>
                                  {({ onClick }) => (
                                    <Menu.Item
                                      leftSection={
                                        model.locked ? (
                                          <IconLockOff size={14} stroke={1.5} />
                                        ) : (
                                          <IconLock size={14} stroke={1.5} />
                                        )
                                      }
                                      onClick={onClick}
                                    >
                                      {model.locked ? 'Unlock' : 'Lock'} model discussion
                                    </Menu.Item>
                                  )}
                                </ToggleLockModel>
                                <ToggleMinorModel modelId={model.id} minor={model.minor}>
                                  {({ onClick }) => (
                                    <Menu.Item
                                      leftSection={<IconBabyCarriage size={14} stroke={1.5} />}
                                      onClick={onClick}
                                    >
                                      {model.minor ? 'Unset as Minor' : 'Set as Minor'}
                                    </Menu.Item>
                                  )}
                                </ToggleMinorModel>
                                {/* Minor already forces (and locks) sfwOnly, so the toggle would
                                    only ever offer an "Unset" the server refuses. */}
                                {!model.minor && (
                                  <ToggleSfwOnlyModel modelId={model.id} sfwOnly={model.sfwOnly}>
                                    {({ onClick }) => (
                                      <Menu.Item
                                        leftSection={<IconShieldCheck size={14} stroke={1.5} />}
                                        onClick={onClick}
                                      >
                                        {model.sfwOnly ? 'Unset as SFW' : 'Set as SFW'}
                                      </Menu.Item>
                                    )}
                                  </ToggleSfwOnlyModel>
                                )}
                                <ToggleLockModelComments
                                  modelId={model.id}
                                  locked={model.meta?.commentsLocked}
                                >
                                  {({ onClick }) => (
                                    <Menu.Item
                                      leftSection={
                                        model.meta?.commentsLocked ? (
                                          <IconLockOff size={14} stroke={1.5} />
                                        ) : (
                                          <IconLock size={14} stroke={1.5} />
                                        )
                                      }
                                      onClick={onClick}
                                    >
                                      {model.meta?.commentsLocked ? 'Unlock' : 'Lock'} model
                                      comments
                                    </Menu.Item>
                                  )}
                                </ToggleLockModelComments>
                                <ToggleSearchableMenuItem
                                  entityType="Model"
                                  entityId={model.id}
                                  key="toggle-searchable-menu-item"
                                />
                                {!model.mode ? (
                                  <>
                                    <Menu.Item
                                      leftSection={<IconArchive size={14} stroke={1.5} />}
                                      onClick={() => handleChangeMode(ModelModifier.Archived)}
                                    >
                                      Archive
                                    </Menu.Item>
                                    <Menu.Item
                                      leftSection={<IconCircleMinus size={14} stroke={1.5} />}
                                      onClick={() => handleChangeMode(ModelModifier.TakenDown)}
                                    >
                                      Take Down
                                    </Menu.Item>
                                  </>
                                ) : (
                                  <Menu.Item
                                    leftSection={<IconReload size={14} stroke={1.5} />}
                                    onClick={() => handleChangeMode(null)}
                                  >
                                    Bring Back
                                  </Menu.Item>
                                )}
                                <Menu.Item
                                  leftSection={<IconArrowsLeftRight size={14} stroke={1.5} />}
                                  onClick={() =>
                                    dialogStore.trigger({
                                      component: TransferModelOwnership,
                                      props: { modelId: model.id },
                                    })
                                  }
                                >
                                  Transfer Ownership
                                </Menu.Item>
                              </>
                            )}
                          </>
                        )}
                        {published && (isOwner || isModerator) && (
                          <>
                            <Menu.Label>Advanced</Menu.Label>
                            <Menu.Item
                              onClick={() =>
                                dialogStore.trigger({
                                  component: MigrateModelToCollection,
                                  props: { modelId: model.id },
                                })
                              }
                            >
                              Migrate to Collection
                            </Menu.Item>
                            {isOwner && model.modelVersions.length > 1 && (
                              <Menu.Item
                                onClick={() =>
                                  dialogStore.trigger({
                                    component: MergeVersions,
                                    props: { modelId: model.id },
                                  })
                                }
                              >
                                Merge Versions
                              </Menu.Item>
                            )}
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </PageGroup>
                </PageGroup>
                <PageGroup ds={ds} gap={4}>
                  <UpdatedText ds={ds}>Updated: {formatDate(model.updatedAt)}</UpdatedText>
                  {category && (
                    <>
                      <TagDivider ds={ds} />
                      <CategoryBadge ds={ds} name={category.name} />
                    </>
                  )}

                  {tags.length > 0 && <TagDivider ds={ds} />}
                  <Collection
                    items={tags}
                    ds={ds}
                    renderItem={(tag) => (
                      <TagBadge ds={ds} name={tag.name} colorScheme={colorScheme} />
                    )}
                  />
                </PageGroup>
              </PageStack>
              {(model.status === ModelStatus.Unpublished || modelDeleted) && (
                <Alert color="red">
                  <Group gap="xs" wrap="nowrap" align="flex-start">
                    <ThemeIcon color="red">
                      <IconExclamationMark />
                    </ThemeIcon>
                    <Text size="md">
                      This model has been {modelDeleted ? 'deleted' : 'unpublished'} and is not
                      visible to the community.
                    </Text>
                  </Group>
                </Alert>
              )}
              {model.status === ModelStatus.UnpublishedViolation && !model.meta?.needsReview && (
                <ModelUnpublishedAlert
                  reason={model.meta?.unpublishedReason}
                  customMessage={model.meta?.customMessage}
                  appealHref={
                    isCreator && features.siteAppealForm
                      ? siteAppealHref(true, { category: AppealCategory.Model, id: model.id })
                      : undefined
                  }
                />
              )}
              {model.status === ModelStatus.UnpublishedViolation && model.meta?.needsReview && (
                <Alert color="yellow">
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon color="yellow">
                      <IconExclamationMark />
                    </ThemeIcon>
                    <Text size="md">
                      This model is currently being reviewed by our moderators. It will be visible
                      to the community once it has been approved.
                    </Text>
                  </Group>
                </Alert>
              )}
              {isOwner && model.meta?.cannotPublish && (
                <Alert color="red">
                  <Group gap="xs" wrap="nowrap" align="flex-start">
                    <ThemeIcon color="red">
                      <IconExclamationMark />
                    </ThemeIcon>
                    <Text size="sm" mt={-3}>
                      Due to the nature of the training data used to create this model, it cannot be
                      Published. If you believe this to be an error, please{' '}
                      <Text component="a" c="blue.4" href="/contact" target="_blank">
                        contact our support team
                      </Text>
                      .
                    </Text>
                  </Group>
                </Alert>
              )}
              {isCreator && (model.minorFlagged || model.poiFlagged) && (
                <ModelMinorFlagAlert model={model} />
              )}
              <OwnerRatingControls entityType="Model" entityId={model.id} isOwner={isCreator} />
              {inaccurate && (
                <Alert color="yellow">
                  <Group gap="xs" wrap="nowrap" align="flex-start">
                    <ThemeIcon color="yellow">
                      <IconExclamationMark />
                    </ThemeIcon>
                    <Text size="md">
                      The images on this {splitUppercase(model.type).toLowerCase()} are inaccurate.
                      Please submit reviews with images so that we can improve this page.
                    </Text>
                  </Group>
                </Alert>
              )}
              {(model.mode === ModelModifier.TakenDown ||
                model.mode === ModelModifier.Archived) && (
                <AlertWithIcon color="blue" icon={<IconExclamationMark />} size="md">
                  {model.mode === ModelModifier.Archived
                    ? 'This model has been archived and is not available for download. You can still share your creations with the community.'
                    : 'The visual assets associated with this model have been taken down. You can still download the resource, but you will not be able to share your creations.'}
                </AlertWithIcon>
              )}
              {model.status === ModelStatus.Published && (
                <FirstPublishCard
                  entityType="model"
                  entityId={model.id}
                  ownerId={model.user.id}
                  publishedAt={model.publishedAt}
                />
              )}
            </PageStack>
            <PageGroup ds={ds} gap={4} nowrap>
              {isOwner ? (
                <>
                  {model.availability !== Availability.Private && (
                    <ButtonTooltip label="Add Version">
                      <Link href={`/models/${model.id}/model-versions/create`}>
                        <LegacyActionIcon variant="light" color="blue">
                          <IconPlus size={14} />
                        </LegacyActionIcon>
                      </Link>
                    </ButtonTooltip>
                  )}

                  {versionCount > 1 && (
                    <ButtonTooltip label="Rearrange Versions">
                      <LegacyActionIcon onClick={toggle}>
                        <IconArrowsLeftRight size={14} />
                      </LegacyActionIcon>
                    </ButtonTooltip>
                  )}
                </>
              ) : null}
              <ModelVersionList
                ds={ds}
                versions={model.modelVersions}
                selected={selectedVersion?.id}
                onVersionClick={(version) => {
                  if (version.id !== selectedVersion?.id) {
                    router.replace(
                      getModelUrl({
                        modelId: model.id,
                        modelName: storedModel?.name ?? model.name,
                        modelVersionId: version.id,
                      }),
                      undefined,
                      { shallow: true }
                    );
                  }
                }}
                showExtraIcons={isOwner || isModerator}
                showToggleCoverage={model.type === ModelType.Checkpoint}
              />
            </PageGroup>
            {!!selectedVersion && (
              <ModelVersionDetails
                ds={ds}
                model={model}
                version={selectedVersion}
                image={image}
                onFavoriteClick={handleToggleFavorite}
              />
            )}
          </PageStack>
          {versionCount > 1 ? (
            <ReorderVersionsModal modelId={model.id} opened={opened} onClose={toggle} />
          ) : null}
        </Container>
        {showRail && <div className={classes.rail}>{canShowRail && <AdUnitSide_1 />}</div>}
      </div>
      {canLoadBelowTheFold && (
        <>
          {/* A sponsored card can run on a page with no suggestions of its own. */}
          {(isOwner || model.hasSuggestedResources || features.creatorPromotions) && (
            <>
              {model.hasSuggestedResources && <AdUnitTopSection />}
              {selectedVersion && (
                <AssociatedModels
                  fromId={model.id}
                  type="Suggested"
                  ownerId={model.user.id}
                  label={
                    <Group gap={8} wrap="nowrap">
                      Suggested Resources{' '}
                      <InfoPopover>
                        <Text size="sm" fw={400}>
                          These are resources suggested by the creator of this model. They may be
                          related to this model or created by the same user.
                        </Text>
                      </InfoPopover>
                    </Group>
                  }
                />
              )}
            </>
          )}
          <AdUnitTopSection />
          <Container size="xl" my="xl">
            <ModelDiscussion
              canDiscuss={canDiscuss}
              onlyEarlyAccess={onlyEarlyAccess}
              modelId={model.id}
              modelUserId={model.user.id}
              locked={model.locked || model.meta?.commentsLocked}
            />
          </Container>
        </>
      )}
      {canLoadBelowTheFold && !model.locked && model.mode !== ModelModifier.TakenDown && (
        <Box id="gallery" mt="md">
          <ModelGallery
            model={model}
            selectedVersionId={selectedVersion?.id}
            modelVersions={model.modelVersions}
            showModerationOptions={isOwner}
            showPOIWarning={model.poi}
            canReview={!versionIsEarlyAccess || currentUser?.isMember || currentUser?.isModerator}
          />
        </Box>
      )}
    </Gated>
  );
}

function AdUnitTopSection() {
  return <AdUnitTop className="bg-gray-1 py-3 dark:bg-dark-6" preserveLayout />;
}
